/**
 * «Min seilas» — deltakerpanel på regattasiden.
 *
 * Identiteten (båtnummer + regatta-passord) registreres på FORSIDEN og leses
 * herfra (read-only). Her velger seileren bare av/på for «Fart & kurs» og
 * «Send posisjon», og åpner kartet i ny fane.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, type TrackPost } from '../lib/api.ts';
import { useGps, GOOD_ACCURACY_M, type GpsFix } from '../lib/useGps.ts';
import { CompassRose } from './CompassRose.tsx';

export function RaceParticipant({
  slug,
  sail,
  password,
}: {
  slug: string;
  sail: string;
  password: string;
}) {
  const gps = useGps();
  const fixRef = useRef<GpsFix | null>(null);
  useEffect(() => {
    fixRef.current = gps.fix;
  }, [gps.fix]);

  const [showSpeed, setShowSpeed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const ready = sail.trim().length > 0 && password.length > 0;

  // Maskinvare-tracker på båten: hvis online kommer fart/kurs/kart derfra i
  // stedet for telefonens GPS.
  const trackerQuery = useQuery({
    queryKey: ['myboat', slug, sail.trim().toUpperCase()],
    queryFn: () => api.myBoatPosition(slug, sail.trim()),
    enabled: ready,
    refetchInterval: 4000,
  });
  const tracker = trackerQuery.data?.tracker ?? null;
  const trackerOnline = !!tracker;

  // Når trackeren er online, slå av telefon-GPS-modusene (unngå dobbeltarbeid).
  useEffect(() => {
    if (trackerOnline && (showSpeed || sending)) {
      setShowSpeed(false);
      setSending(false);
      if (gps.status === 'active') gps.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackerOnline]);

  // Hvis identiteten forsvinner (avregistrert), stopp alt.
  useEffect(() => {
    if (!ready && sending) {
      setSending(false);
      if (!showSpeed && gps.status === 'active') gps.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  function stopGpsIfIdle(speedOn: boolean, sendOn: boolean) {
    if (!speedOn && !sendOn && gps.status === 'active') gps.stop();
  }

  function toggleSpeed() {
    if (!ready) return;
    const next = !showSpeed;
    setShowSpeed(next);
    if (next) {
      if (gps.status !== 'active') gps.start();
    } else {
      stopGpsIfIdle(false, sending);
    }
  }

  function toggleSending() {
    if (!ready) return;
    if (!sending) {
      setErr(null);
      setInfo(null);
      setSent(0);
      if (gps.status !== 'active') gps.start();
      setSending(true);
    } else {
      setSending(false);
      stopGpsIfIdle(showSpeed, false);
    }
  }

  // Send-løkke
  useEffect(() => {
    if (!sending) return;
    const id = setInterval(async () => {
      const f = fixRef.current;
      if (!f || f.accuracy == null || f.accuracy > GOOD_ACCURACY_M) return;
      const body: TrackPost = {
        raceSlug: slug,
        sailNumber: sail.trim().toUpperCase(),
        password,
        lat: f.lat,
        lon: f.lon,
        sogKn: f.sogKn,
        cog: f.cog,
        accuracy: f.accuracy,
        recordedAt: f.recordedAt,
      };
      try {
        const r = await api.postTrack(body);
        setSent((c) => c + 1);
        setErr(null);
        if (r.trackedByDevice && !r.finished) {
          setInfo('ℹ️ Båten spores av tracker — telefon-posisjon brukes ikke nå.');
        } else if (!r.finished) {
          setInfo(null);
        }
        if (r.finished) {
          setSending(false);
          stopGpsIfIdle(showSpeed, false);
          setInfo('🏁 Du er registrert i mål — sporing er stoppet.');
        }
      } catch (e) {
        const er = e as ApiError;
        if (er.status === 401 || er.status === 403 || er.status === 409) {
          setErr(er.message);
          setSending(false);
          stopGpsIfIdle(showSpeed, false);
        } else {
          setErr(er.message ?? 'Kunne ikke sende posisjon');
        }
      }
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sending, slug, sail, password, showSpeed]);

  const fix = gps.fix;

  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 mb-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-x-2 gap-y-1">
        <h2 className="font-semibold">🧭 Min seilas</h2>
        {ready ? (
          <span className="font-semibold">
            Deltar som <span className="text-sky-300">{sail.trim().toUpperCase()}</span>
          </span>
        ) : (
          <Link to="/" className="text-xs text-sky-400">Registrer deg på forsiden ↗</Link>
        )}
      </div>

      {!ready && (
        <p className="text-xs text-slate-500">
          Skriv båtnummer og regatta-passord under «📋 Min registrering» på forsiden for å bruke
          fart/kurs og sporing.
        </p>
      )}

      {tracker ? (
        <>
          {/* Tracker-modus: fart/kurs fra båtens GPS-tracker */}
          <div className="grid grid-cols-2 gap-2 items-center">
            <div className="text-center min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-emerald-400">🛰️ SOG</div>
              <div
                className="font-mono font-bold tabular-nums leading-none"
                style={{ fontSize: 'clamp(2.5rem, 14vw, 4.5rem)' }}
              >
                {tracker.sogKn != null ? tracker.sogKn.toFixed(1) : '—'}
              </div>
              <div className="text-sm text-slate-300">
                knop
                {tracker.cog != null && (
                  <span className="text-slate-500">
                    {' '}
                    · {Math.round(tracker.cog).toString().padStart(3, '0')}°
                  </span>
                )}
              </div>
            </div>
            <CompassRose cog={tracker.cog} className="w-full max-w-[190px] mx-auto block" />
          </div>
          <button
            type="button"
            onClick={() => window.open(`/minkart/${slug}`, 'rc-kart')}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-sky-700 hover:bg-sky-600 text-sm font-semibold"
          >
            🧭 Åpne mitt sjøkart
          </button>
          <p className="text-xs text-emerald-300/80 text-center">
            Fart, kurs og kart hentes fra båtens GPS-tracker
            {tracker.satellites != null ? ` · ${tracker.satellites} sat` : ''}.
          </p>
        </>
      ) : (
        <>
      {showSpeed && (
        <div className="grid grid-cols-2 gap-2 items-center">
          <div className="text-center min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">SOG</div>
            <div
              className="font-mono font-bold tabular-nums leading-none"
              style={{ fontSize: 'clamp(2.5rem, 14vw, 4.5rem)' }}
            >
              {gps.goodFix && fix ? fix.sogKn.toFixed(1) : '—'}
            </div>
            <div className="text-sm text-slate-300">
              knop
              {gps.goodFix && fix && fix.cog != null && (
                <span className="text-slate-500"> · {Math.round(fix.cog).toString().padStart(3, '0')}°</span>
              )}
            </div>
          </div>
          <CompassRose
            cog={gps.goodFix && fix ? fix.cog : null}
            className="w-full max-w-[190px] mx-auto block"
          />
        </div>
      )}

      {(showSpeed || sending) && !gps.goodFix && (
        <p className="text-xs text-amber-300 text-center">
          📡 Venter på presis GPS{fix?.accuracy != null ? ` (±${Math.round(fix.accuracy)} m)` : ''}…{' '}
          <a
            href="/hjelp/posisjon"
            target="rc-hjelp"
            className="underline text-amber-200"
          >
            slik slår du på «Nøyaktig posisjon» ↗
          </a>
        </p>
      )}

      {/* Handlingsrad: fart/kurs · posisjon · sjøkart — kompakt og like høye */}
      <div className="grid grid-cols-3 gap-1.5">
        <button
          onClick={toggleSpeed}
          disabled={!ready && !showSpeed}
          className={`flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[11px] font-semibold leading-tight transition disabled:opacity-40 disabled:cursor-not-allowed ${
            showSpeed ? 'bg-emerald-600' : 'bg-slate-800 hover:bg-slate-700'
          }`}
        >
          <span className="text-lg leading-none">📊</span>
          <span>Fart/kurs</span>
        </button>
        <button
          onClick={toggleSending}
          disabled={!ready && !sending}
          className={`flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[11px] font-semibold leading-tight transition disabled:opacity-40 disabled:cursor-not-allowed ${
            sending ? 'bg-emerald-600' : 'bg-slate-800 hover:bg-slate-700'
          }`}
        >
          <span className="text-lg leading-none">📡</span>
          <span>Posisjon</span>
        </button>
        <button
          type="button"
          onClick={() => window.open(`/minkart/${slug}`, 'rc-kart')}
          className="flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[11px] font-semibold leading-tight bg-slate-800 hover:bg-slate-700 text-sky-300"
        >
          <span className="text-lg leading-none">🧭</span>
          <span>Sjøkart</span>
        </button>
      </div>
        </>
      )}

      {sending && (
        <div className="text-xs text-slate-400 text-center">
          {sent > 0 ? `✅ ${sent} posisjoner sendt` : 'Klar — sender når GPS har god lås'}
          {gps.wakeLockOn && ' · 📱 skjerm-på'}
        </div>
      )}
      {info && <div className="text-xs text-emerald-300 text-center">{info}</div>}
      {err && <div className="text-xs text-red-300 text-center">{err}</div>}
    </div>
  );
}
